import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { City, Country, Province } from "../models/Country";
import { RootState } from "../store";
import { getCities, getCountries, getProvinces } from "../repository/locationRepository";

export type LocationState = {
    countries: Country[],
    provinces: Record<string, Province[]>,
    cities: Record<string, City[]>
}

const initialState: LocationState = {
    countries: [],
    provinces: {},
    cities: {}
};

export const locationSlice = createSlice({
    name: 'location',
    initialState: initialState,
    reducers: {
        setCountries: (state, action: PayloadAction<Country[]>) => {
            state.countries = action.payload;
        },
        setProvinces: (state, action: PayloadAction<{ countryId: string, provinces: Province[] }>) => {
            state.provinces[action.payload.countryId] = action.payload.provinces;
        },
        setCities: (state, action: PayloadAction<{ provinceId: string, cities: City[] }>) => {
            state.cities[action.payload.provinceId] = action.payload.cities;
        }
    }
});

export const {
    setCountries,
    setProvinces,
    setCities
} = locationSlice.actions;

export const downloadCountries = createAsyncThunk<void, void, { state: RootState }>(
    "location/downloadCountries",
    async (_, thunkAPI) => {
        const {
            countries
        } = thunkAPI.getState().location;

        if (countries.length) {
            return;
        }

        getCountries()
            .then((result) => {
                result.sort((a, b) => a.name.localeCompare(b.name));
                thunkAPI.dispatch(setCountries(result));
            })
            .catch((error) => {
                console.error("List of countries could not be fetched.");
            });
    }
);

export const downloadProvinces = createAsyncThunk<void, { countryId: string }, { state: RootState }>(
    "location/downloadProvinces",
    async (input, thunkAPI) => {
        const {
            provinces
        } = thunkAPI.getState().location;

        const existing = provinces[input.countryId];
        if (existing && existing.length) {
            return;
        }

        getProvinces({ countryId: input.countryId })
            .then((result) => {
                result.sort((a, b) => a.name.localeCompare(b.name));
                thunkAPI.dispatch(
                    setProvinces({
                        countryId: input.countryId,
                        provinces: result
                    })
                );
            })
            .catch((error) => {
                console.error("List of provinces could not be fetched.");
            });
    }
);

export const downloadCities = createAsyncThunk<void, { provinceId: string }, { state: RootState }>(
    "location/downloadCities",
    async (input, thunkAPI) => {
        const {
            cities
        } = thunkAPI.getState().location;

        const existing = cities[input.provinceId];
        if (existing && existing.length) {
            return;
        }

        getCities({ provinceId: input.provinceId })
            .then((result) => {
                result.sort((a, b) => a.name.localeCompare(b.name));
                thunkAPI.dispatch(
                    setCities({
                        provinceId: input.provinceId,
                        cities: result
                    })
                );
            })
            .catch((error) => {
                console.error("List of cities could not be fetched.");
            });
    }
);

export default locationSlice.reducer;